import { useSeo } from "@/lib/site";
import { MaskedLines, FadeIn, Reveal } from "@/components/site/Motion";
import { Section, Label, H2, Paras, CtaBand, CtaButton, BookMeeting, CtaRow } from "@/components/site/Blocks";
import { Awards } from "@/components/home/Awards";

const TEAM = [
  ["Dhruv Mathur", "dhruv", "CEO"],
  ["Jakeline Diogenes", "jakeline", "Head of Strategy"],
  ["Pratik Mathur", "pratik", "Head of Video"],
  ["Siobhan Bogan", "siobhan", "Marketing Manager"],
  ["Pratima Agrawal", "pratima", "Digital Marketing Manager"],
  ["Lorna Anne", "lorna", "Account Manager"],
];

const STATS = [["10+", "people in our team"], ["2", "offices — London and Dublin"], ["7", "industry awards"]];

const Member = ({ name, file, role, i }) => (
  <Reveal delay={i * 0.08} data-testid={`team-member-${i}`}>
    <figure className="group">
      <div className="relative aspect-[4/5] overflow-hidden rounded-[24px] bg-[#e9e7f1]">
        <img src={`/team/${file}.webp`} alt={name} loading="lazy"
          className="h-full w-full object-cover object-top grayscale contrast-[1.08] transition-[filter,transform] duration-700 group-hover:scale-[1.04] group-hover:grayscale-0" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0716]/60 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      </div>
      <figcaption className="mt-4 flex items-start justify-between gap-3">
        <div>
          <p className="font-display text-xl font-medium text-slate-900">{name}</p>
          <p data-testid={`team-member-role-${i}`} className="mt-1 text-[14px] text-violet-700">{role}</p>
        </div>
        <span className="mt-1.5 font-mono text-[11px] text-slate-500">{String(i + 1).padStart(2, "0")}</span>
      </figcaption>
    </figure>
  </Reveal>
);

export default function About() {
  useSeo("About Us | SEO & AI Search Visibility Agency | Perfect Digitals", "Perfect Digitals is a team of 10+ search specialists in London and Dublin, with experience helping brands across a wide range of sectors and industries.");
  return (
    <div data-testid="about-page">
      <section className="grain relative overflow-hidden bg-ink pb-20 pt-36 text-white sm:pb-28 sm:pt-44">
        <div aria-hidden="true" className="grid-lines absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]" />
        <div aria-hidden="true" className="absolute right-[-10%] top-[-20%] h-[640px] w-[640px] rounded-full bg-violet-700/25 blur-[140px]" />
        <div className="wrap relative">
          <FadeIn className="font-mono text-[11px] uppercase tracking-[0.25em] text-violet-300">About Perfect Digitals</FadeIn>
          <MaskedLines testid="about-title" lines={["Experience Across", <span className="font-serif-i text-gradient">Every Kind</span>, "of Brand."]} className="mt-7 font-display text-[2.8rem] font-medium leading-[0.95] sm:text-7xl lg:text-[6rem]" />
          <FadeIn delay={0.6} className="mt-10 grid gap-10 lg:grid-cols-12">
            <div className="space-y-4 text-[15px] leading-relaxed text-slate-300 sm:text-base lg:col-span-6">
              <p>Perfect Digitals is an SEO and AI Search Visibility agency with offices in London and Dublin.</p>
              <p>Over the years we have built a wealth of experience working with brands across a wide variety of sectors and industries. Every market searches differently, and that breadth of experience shapes how we approach each new client.</p>
            </div>
            <CtaRow className="lg:col-span-5 lg:col-start-8 lg:justify-end lg:self-end">
              <BookMeeting testid="about-book-meeting" />
              <CtaButton label="Contact Us" variant="ghost" to="/contact" testid="about-contact-cta" />
            </CtaRow>
          </FadeIn>
          <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 sm:grid-cols-3" data-testid="about-stats">
            {STATS.map(([v, l], i) => (
              <Reveal key={l} delay={i * 0.08} className="bg-[#0B0716] p-8">
                <p className="font-display text-6xl font-medium text-gradient sm:text-7xl">{v}</p>
                <p className="mt-3 text-[15px] text-slate-300">{l}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Section testid="about-sectors-section">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal><Label n="01">Experience</Label></Reveal>
            <Reveal delay={0.05}><H2 className="mt-6">Brands Across <span className="font-serif-i text-gradient-dark">Sectors &amp; Industries</span></H2></Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal><Paras items={[
              "We have worked with local businesses, national brands and international organisations, each with its own competitors, customers and search behaviour.",
              "That experience means we understand that a strategy which works for one market rarely transfers unchanged to another. We start with how your customers search, then build the SEO and AI Search Visibility strategy around it.",
            ]} /></Reveal>
          </div>
        </div>
      </Section>

      <Section tone="subtle" testid="about-team-section">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-6">
            <Reveal><Label n="02">Our team</Label></Reveal>
            <Reveal delay={0.05}><H2 className="mt-6">A Team of <span className="font-serif-i text-gradient-dark">10+ People</span></H2></Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-5 lg:col-start-8">
            <Paras items={["Behind every campaign is a team of more than 10 people working across London, Dublin and beyond. Here are some of the faces you may meet."]} />
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3" data-testid="team-grid">
          {TEAM.map(([n, f, r], i) => <Member key={f} name={n} file={f} role={r} i={i} />)}
        </div>
      </Section>

      <Awards />
      <div className="bg-[#FAFAFC] pb-24 sm:pb-32" />

      <CtaBand
        testid="about-final-cta"
        title={["Let's Talk About", <span className="font-serif-i text-gradient">Your Search</span>, "Visibility"]}
        paras={["Book a meeting with our team or get in touch to discuss your visibility across Google and AI search."]}
        ctas={<><BookMeeting testid="about-final-book-meeting" /><CtaButton label="Contact Us" variant="ghost" to="/contact" testid="about-final-contact" /></>}
      />
    </div>
  );
}
